import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  AttributeDefinition,
  FileExtensionTypes,
  FormTypes,
  ImageTypes,
  LocationTypes,
  OptionDefinition,
  ScanTypes,
  VoltageClass,
} from '../../../../models/section-definition.model';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ToastrService } from 'ngx-toastr';
import { parseEnumValue } from '../../../../shared/enum-utils';
import { FormTypeLabelMap, FormTypeMap } from '../../../../enums/enum-mappings';

@Component({
  selector: 'lib-add-new-network-device-field',
  templateUrl: './add-new-network-device-field.component.html',
  styleUrls: ['./add-new-network-device-field.component.scss'],
  standalone: false,
})
export class AddNewNetworkDeviceFieldComponent {
  @Input() attributes: AttributeDefinition[] = [];
  @Output() closeModalEvent = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();
  @Input() attrId?: number;
  @Input() showVoltageClass: boolean = true;
  attribute: AttributeDefinition = new AttributeDefinition();
  dropdownAttributes: AttributeDefinition[] = [];
  selectedFieldType: string = 'Dropdown';
  format: boolean | null = null;
  isFocused: boolean = false;
  isPlaceholderFocused: boolean = false;
  hasMaxLength: boolean = false;
  includeTime: boolean = false;
  formatSelection: boolean = false;
  selectionTypes: { [key: string]: boolean } = {
    Multiple: false,
    Single: false,
  };
  coordinatesSelected: boolean = false;
  postalCodeSelected: boolean = false;
  qrCodeSelected: boolean = false;
  barCodeSelected: boolean = false;
  cameraSelected: boolean = false;
  fileSelected: boolean = false;

  activeConditionalDropdownIndex: number | null = null;
  selectedExtensionTypes: FileExtensionTypes[] = [];
  allowNumbers: boolean = false;
  hvEnabled: boolean = true;
  mvEnabled: boolean = true;
  lvEnabled: boolean = true;

  extensionTypes = Object.entries(FileExtensionTypes)
    .filter(([key, value]) => isNaN(Number(key)))
    .map(([key, value]) => ({ key, value: Number(value) }));

  constructor(private toastr: ToastrService) {}

  ngOnInit(): void {
    if (this.attrId) {
      this.populateExistingFields();
    } else {
      this.attribute.fieldType = this.getFormType(this.selectedFieldType);
      if (this.attribute.fieldType == FormTypes.Dropdown) {
        this.attribute.optionDefinitions = this.getDefaultOptionDefinitions();
      } else if (this.attribute.fieldType == FormTypes.ConditionalDropdown) {
        this.attribute.optionDefinitions = [{ id: 0, value: '', displayOrder: 1 }];
        var dropdownAttribute = new AttributeDefinition();
        dropdownAttribute.optionDefinitions =
          this.getDefaultOptionDefinitions(this.attribute.optionDefinitions[0]);
        dropdownAttribute.fieldType = FormTypes.ChildDropdown;
        dropdownAttribute.guid = this.attribute.guid;
        this.dropdownAttributes.push(dropdownAttribute);
      }
      this.selectRequired(true);
      this.selectDefaultState(true);
      this.selectionTypes['Single'] = true;
    }
  }

  getFormType(label: string): FormTypes {
    return FormTypeMap[label] ?? FormTypes.Text;
  }

  selectFieldType(fieldType: string) {
    if (!this.attrId) {
      this.attribute = new AttributeDefinition();
      this.dropdownAttributes = [];
      this.attribute.fieldType = this.getFormType(fieldType);
      if (this.attribute.fieldType == FormTypes.Dropdown) {
        this.attribute.optionDefinitions = this.getDefaultOptionDefinitions();
      } else if (this.attribute.fieldType == FormTypes.ConditionalDropdown) {
        this.attribute.optionDefinitions = [{ id: 0, value: '', displayOrder: 1 }];
        var dropdownAttribute = new AttributeDefinition();
        dropdownAttribute.optionDefinitions =
          this.getDefaultOptionDefinitions(this.attribute.optionDefinitions[0]);
        dropdownAttribute.fieldType = FormTypes.ChildDropdown;
        dropdownAttribute.guid = this.attribute.guid;
        dropdownAttribute.displayOrder = this.dropdownAttributes.length + 1;
        this.dropdownAttributes.push(dropdownAttribute);
      } else if (this.attribute.fieldType == FormTypes.DateTime) {
        this.includeTime = true;
        this.formatSelection = true;
        this.attribute.is24HoursFormat = true;
      } else if (this.attribute.fieldType == FormTypes.MultiSelect)
        this.attribute.isSingleType = false;

      this.selectedFieldType = fieldType;
      this.clearPlaceholderText();
      this.clearFieldName();
      this.selectRequired(true);
      this.hasMaxLength = true;
      this.attribute.maxNumberValue = 0;
      this.hvEnabled = true;
      this.mvEnabled = true;
      this.lvEnabled = true;
    }
  }

  addDropdownOption() {
    this.attribute.optionDefinitions.push({
      id: 0,
      value: '',
      displayOrder: this.attribute.optionDefinitions.length + 1,
      parentOption: undefined,
    });
  }

  addChildOption(position: number, parent?: OptionDefinition) {
    this.dropdownAttributes[position].optionDefinitions.push({
      id: 0,
      value: '',
      displayOrder:
        this.dropdownAttributes[position].optionDefinitions.length + 1,
      parentOption: parent,
    });
  }

  addParentOption() {
    this.attribute.optionDefinitions.push({
      id: 0,
      value: '',
      displayOrder: this.attribute.optionDefinitions.length + 1,
    });
    var dropdownAttribute = new AttributeDefinition();
    dropdownAttribute.optionDefinitions = this.getDefaultOptionDefinitions(this.attribute.optionDefinitions[this.attribute.optionDefinitions.length - 1]);
    dropdownAttribute.fieldType = FormTypes.ChildDropdown;
    dropdownAttribute.guid = this.attribute.guid;
    dropdownAttribute.parentAttributeId = this.attribute.id || undefined;
    dropdownAttribute.displayOrder = this.dropdownAttributes.length + 1;
    this.dropdownAttributes.push(dropdownAttribute);
  }

  getDefaultOptionDefinitions(parent?: OptionDefinition) {
    return [
      { id: 0, value: '', displayOrder: 1, parentOption: parent },
      { id: 0, value: '', displayOrder: 2, parentOption: parent },
      { id: 0, value: '', displayOrder: 3, parentOption: parent },
    ];
  }

  selectRequired(value: boolean) {
    this.attribute.isRequired = value;
  }

  selectDefaultState(value: boolean) {
    this.attribute.defaultState = value;
  }

  clearFieldName() {
    this.attribute.label = '';
  }

  clearPlaceholderText() {
    this.attribute.placeholderText = '';
  }

  updateSelectionType(type: 'Single' | 'Multiple', event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.checked) {
      event.preventDefault();
      input.checked = true;
      return;
    }

    this.attribute.isSingleType = type === 'Single';
  }

  updateLocationType(type: 'Coordinates' | 'PostalCode', event: Event): void {
    const input = event.target as HTMLInputElement;

    event.preventDefault();

    if (type === 'Coordinates') {
      this.coordinatesSelected = input.checked;
    } else if (type === 'PostalCode') {
      this.postalCodeSelected = input.checked;
    }

    this.attribute.locationType =
      this.coordinatesSelected && this.postalCodeSelected
        ? LocationTypes.Both
        : this.coordinatesSelected
        ? LocationTypes.Coordinates
        : LocationTypes.PostalCode;
  }

  updateScanType(type: 'QRCode' | 'BarCode', event: Event): void {
    const input = event.target as HTMLInputElement;

    event.preventDefault();

    if (type === 'QRCode') {
      this.qrCodeSelected = input.checked;
    } else if (type === 'BarCode') {
      this.barCodeSelected = input.checked;
    }

    this.attribute.scanType =
      this.qrCodeSelected && this.barCodeSelected
        ? ScanTypes.Both
        : this.qrCodeSelected
        ? ScanTypes.QRCode
        : ScanTypes.BarCode;
  }

  updateImageType(type: 'Camera' | 'File', event: Event): void {
    const input = event.target as HTMLInputElement;

    event.preventDefault();

    if (type === 'Camera') {
      this.cameraSelected = input.checked;
    } else if (type === 'File') {
      this.fileSelected = input.checked;
    }

    this.attribute.imageType =
      this.cameraSelected && this.fileSelected
        ? ImageTypes.Both
        : this.cameraSelected
        ? ImageTypes.Camera
        : ImageTypes.File;
  }

  toggleType(value: boolean) {
    this.includeTime = value;
    if (!value) {
      this.attribute.is24HoursFormat = undefined;
    } else {
      this.toggleFormat(true);
    }
  }

  toggleFormat(value: boolean) {
    this.formatSelection = value;
    this.attribute.is24HoursFormat = value;
  }

  toggleMaxLength(value: boolean) {
    this.hasMaxLength = value;
    if (!value) {
      this.attribute.maxNumberValue = null;
      this.attribute.minNumberValue = null;
    }
  }

  increaseLength() {
    this.attribute.maxNumberValue!++;
  }

  decreaseLength() {
    if (this.hasMaxLength && this.attribute.maxNumberValue! > 0) {
      this.attribute.maxNumberValue!--;
    }
  }

  validateLength() {
    if (this.attribute.maxNumberValue! < 0) this.attribute.maxNumberValue = 0;
  }

  closeModal() {
    this.closeModalEvent.emit();
  }

  onBackdropClick() {
    this.closeModal();
  }

  saveField() {
    if (!this.validate()) {
      return;
    }
    if (!this.attrId) {
      this.attributes.push(this.attribute);
      this.attribute.displayOrder = this.attributes.length;
    }

    if (
      parseEnumValue(FormTypes, this.attribute.fieldType) ===
      FormTypes.ConditionalDropdown
    ) {
      let order = this.attribute.displayOrder;
      this.dropdownAttributes.forEach(attr => {
        order++; 
        attr.displayOrder = order;
      });

      this.attributes.push(...this.dropdownAttributes);
    }

    if (
      parseEnumValue(FormTypes, this.attribute.fieldType) === FormTypes.File
    ) {
      this.attribute.fileExtensionTypes = this.selectedExtensionTypes;
    }

    this.closeModal();

    this.saved.emit();
  }

  validate() {
    switch (parseEnumValue(FormTypes, this.attribute.fieldType)!) {
      case FormTypes.Text:
      case FormTypes.TextDescription:
        // case FormTypes.Alphanumeric:
        return this.validateTextField();
      case FormTypes.Dropdown:
      case FormTypes.SingleSelect:
      case FormTypes.MultiSelect:
        return this.validateDropdownField(this.attribute);
      case FormTypes.Bool:
        return this.validateToggleField();
      case FormTypes.Number:
        return this.validateNumberField();
      case FormTypes.ConditionalDropdown:
        return this.validateConditionalDropdownField();
      case FormTypes.DateTime:
        return this.validateDateField();
      case FormTypes.Location:
        return this.validateLocationField();
      case FormTypes.Scan:
        return this.validateScanField();
      case FormTypes.File:
        return this.validateFileField();
      case FormTypes.Image:
        return this.validateImageField();

      default:
        return false;
    }
  }

  validateConditionalDropdownField() {
    if (!this.attribute.label) {
      this.toastr.error('Field name is mandatory!');
      return false;
    }

    for (var i = 0; i < this.dropdownAttributes.length; i++) {
      var res = this.validateDropdownField(this.dropdownAttributes[i]);
      if (!res) {
        return;
      }
    }
    return true;
  }

  validateDateField() {
    if (!this.attribute.label) {
      this.toastr.error('Field name is mandatory!');
      return false;
    }

    return true;
  }

  validateLocationField() {
    if (!this.attribute.label) {
      this.toastr.error('Field name is mandatory!');
      return false;
    } else if (!this.coordinatesSelected && !this.postalCodeSelected) {
      this.toastr.error('Choose at least one location type!');
      return false;
    }

    return true;
  }

  validateScanField() {
    if (!this.attribute.label) {
      this.toastr.error('Field name is mandatory!');
      return false;
    } else if (!this.qrCodeSelected && !this.barCodeSelected) {
      this.toastr.error('Choose at least one scan type!');
      return false;
    }

    return true;
  }

  validateToggleField() {
    if (!this.attribute.label) {
      this.toastr.error('Field name is mandatory!');
      return false;
    }
    return true;
  }
  validateTextField() {
    if (!this.attribute.label) {
      this.toastr.error('Field name is mandatory!');
      return false;
    }
    if (this.hasMaxLength && !this.attribute.maxNumberValue) {
      this.toastr.error('Max length value is missing!');
      return false;
    }

    if (this.allowNumbers) {
      this.attribute.regularExpression ='^.*$';
    } else {
      this.attribute.regularExpression ='^[^\\d]*$';
    }

    return true;
  }

  validateFileField() {
    if (!this.attribute.label) {
      this.toastr.error('Field name is mandatory!');
      return false;
    }
    if (this.hasMaxLength && !this.attribute.maxNumberValue) {
      this.toastr.error('Max size value is missing!');
      return false;
    }
    if (this.selectedExtensionTypes.length == 0) {
      this.toastr.error('Choose at least one suitable extension!');
      return false;
    }

    return true;
  }

  validateImageField() {
    if (!this.attribute.label) {
      this.toastr.error('Field name is mandatory!');
      return false;
    } else if (!this.cameraSelected && !this.fileSelected) {
      this.toastr.error('Choose at least one image upload type!');
      return false;
    } else if (this.hasMaxLength && !this.attribute.maxNumberValue) {
      this.toastr.error('Max size value is missing!');
      return false;
    }

    return true;
  }

  validateNumberField() {
    if (!this.attribute.label) {
      this.toastr.error('Field name is mandatory!');
      return false;
    }
    if (!this.attribute.placeholderText) {
      this.toastr.error('Placeholder text is mandatory!');
      return false;
    }
    if (this.hasMaxLength && !this.attribute.maxNumberValue) {
      this.toastr.error('Max length value is missing!');
      return false;
    }

    return true;
  }

  validateDropdownField(attribute: AttributeDefinition) {
    if (!attribute.label) {
      this.toastr.error('Field name is mandatory!');
      return false;
    }
    if (
      !attribute.optionDefinitions ||
      attribute.optionDefinitions.length == 0
    ) {
      this.toastr.error('Options are missing!');
      return false;
    }
    const seenValues = new Set<string>();

    for (let i = 0; i < attribute.optionDefinitions.length; i++) {
      const option = attribute.optionDefinitions[i];

      if (!option.value || option.value.trim() === '') {
        this.toastr.error(`Option ${i + 1} is empty!`);
        return false;
      }

      const trimmedValue = option.value.trim();
      if (seenValues.has(trimmedValue)) {
        this.toastr.error(`Duplicate option found: "${trimmedValue}"`);
        return false;
      }

      seenValues.add(trimmedValue);
    }

    return true;
  }

  drop(event: CdkDragDrop<string[]>, optionDefinitions: any[]) {
    moveItemInArray(optionDefinitions, event.previousIndex, event.currentIndex);

    optionDefinitions.forEach((option, index) => {
      option.displayOrder = index;
    });
  }

  deleteConditionalOption(attributeDefinition: any, index: number): void {
    attributeDefinition.optionDefinitions.splice(index, 1);
    this.activeConditionalDropdownIndex = null;
  }

  toggleConditionalDropdown(index: number): void {
    this.activeConditionalDropdownIndex =
      this.activeConditionalDropdownIndex === index ? null : index;
  }

  populateExistingFields() {
    this.attribute = this.attributes.find((x) => x.id == this.attrId)!;

    this.selectedFieldType =
      FormTypeLabelMap[this.attribute.fieldType!.toString()];

    if (
      parseEnumValue(FormTypes, this.attribute.fieldType) == FormTypes.Location
    ) {
      let locationType = parseEnumValue(
        LocationTypes,
        this.attribute.locationType
      );
      this.coordinatesSelected =
        locationType == LocationTypes.Both ||
        locationType == LocationTypes.Coordinates;
      this.postalCodeSelected =
        locationType == LocationTypes.Both ||
        locationType == LocationTypes.PostalCode;
    }

    if (parseEnumValue(FormTypes, this.attribute.fieldType) == FormTypes.Scan) {
      let scanType = parseEnumValue(ScanTypes, this.attribute.scanType);
      this.qrCodeSelected =
        scanType == ScanTypes.Both || scanType == ScanTypes.QRCode;
      this.barCodeSelected =
        scanType == ScanTypes.Both || scanType == ScanTypes.BarCode;
    }

    this.hasMaxLength = this.attribute.maxNumberValue ? true : false;
    if (
      parseEnumValue(FormTypes, this.attribute.fieldType) == FormTypes.Dropdown
    ) {
      this.selectionTypes['Single'] = this.attribute.isSingleType!;
      this.selectionTypes['Multiple'] = !this.attribute.isSingleType!;
    }

    if (parseEnumValue(FormTypes, this.attribute.fieldType) == FormTypes.File) {
      this.selectedExtensionTypes = this.attribute.fileExtensionTypes ?? [];
    }

    if (
      parseEnumValue(FormTypes, this.attribute.fieldType) == FormTypes.DateTime
    ) {
      if (this.attribute.is24HoursFormat != null) {
        this.includeTime = true;
        this.formatSelection = this.attribute.is24HoursFormat;
      }
    }

    if (
      parseEnumValue(FormTypes, this.attribute.fieldType) ==
      FormTypes.ConditionalDropdown
    ) {
      this.dropdownAttributes = this.attributes.filter(
        (x) => x.parentAttributeId && x.parentAttributeId == this.attribute.id
      );
    }

    if (
      parseEnumValue(FormTypes, this.attribute.fieldType) == FormTypes.Image
    ) {
      let imageType = parseEnumValue(ImageTypes, this.attribute.imageType);
      this.cameraSelected =
        imageType == ImageTypes.Both || imageType == ImageTypes.Camera;
      this.fileSelected =
        imageType == ImageTypes.Both || imageType == ImageTypes.File;
    }

    this.hvEnabled = !this.attribute.excludedVoltages?.some(v => (v as any) === 'HV' || Number(v) === VoltageClass.HV);
    this.mvEnabled = !this.attribute.excludedVoltages?.some(v => (v as any) === 'MV' || Number(v) === VoltageClass.MV);
    this.lvEnabled = !this.attribute.excludedVoltages?.some(v => (v as any) === 'LV' || Number(v) === VoltageClass.LV);
  }

  selectExtensionType(type: number, event: Event) {
    const input = event.target as HTMLInputElement;

    if (input.checked) this.selectedExtensionTypes.push(type);
    else
      this.selectedExtensionTypes = this.selectedExtensionTypes.filter(
        (x) => parseEnumValue(FileExtensionTypes, x) != type
      );
  }

  populateChecked(type: string) {
    return this.selectedExtensionTypes.find((x) => x.toString() == type);
  }

  toggleAllowNumbers() {
    this.allowNumbers = !this.allowNumbers;
  }

  updateVoltageClass(voltage: 'HV' | 'MV' | 'LV', event: Event): void {
    const input = event.target as HTMLInputElement;
    event.preventDefault();

    if (voltage === 'HV') this.hvEnabled = input.checked;
    else if (voltage === 'MV') this.mvEnabled = input.checked;
    else if (voltage === 'LV') this.lvEnabled = input.checked;

    const excluded: VoltageClass[] = [];
    if (!this.hvEnabled) excluded.push(VoltageClass.HV);
    if (!this.mvEnabled) excluded.push(VoltageClass.MV);
    if (!this.lvEnabled) excluded.push(VoltageClass.LV);
    this.attribute.excludedVoltages = excluded;
  }

  clearOption(num: number) {
    this.attribute.optionDefinitions.splice(num, 1);
  }

  clearConditionalDropwodnOption(pos: number, num: number) {
    this.dropdownAttributes[pos].optionDefinitions.splice(num, 1);
  }
}
